import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

interface IndicatorStyle {
  left: number;
  width: number;
}

export function useHeaderAnimation(recalcKey?: unknown) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorStyle>({
    left: 0,
    width: 0,
  });
  const location = useLocation();

  useEffect(() => {
    const computeLeftRelative = (active: HTMLElement, container: HTMLElement | null) => {
      const walk = (node: HTMLElement | null, acc: number): number => {
        if (!node || node === container) return acc;
        return walk(node.offsetParent as HTMLElement | null, acc + (node.offsetLeft || 0));
      };

      return walk(active, 0);
    };

    const computeFor = (
      container: React.RefObject<HTMLDivElement | null>,
      setter: (s: IndicatorStyle) => void,
      moveRightWhenEmpty = false,
    ) => {
      if (!container || !container.current) {
        setter({ left: 0, width: 0 });
        return;
      }

      const active = container.current.querySelector('a.active') as HTMLElement | null;

      if (!active && moveRightWhenEmpty) {
        setter({ left: container.current.offsetWidth, width: 0 });
        return;
      }

      if (!active && !moveRightWhenEmpty) {
        setter({ left: 0, width: 0 });
        return;
      }

      if (!active) {
        return;
      }

      const left = computeLeftRelative(active, container.current);
      setter({ left, width: active.offsetWidth });
    };

    const recalc = () => {
      setTimeout(() => {
        computeFor(tabsRef, setIndicatorPosition, false);
      }, 100);
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [location.pathname, recalcKey]);

  return {
    tabsRef,
    indicatorPosition,
    setIndicatorPosition,
  };
}
